import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { distinctUntilChanged, Subscription, tap } from 'rxjs';
import { Filter, FilterOutput } from '../../models/filters.model';
import { DateFilterOutput } from '../../models/date-filter-output.model';
import { DateFilterComponent } from '../date-filter/date-filter.component';
import { FormControl, FormGroup } from '@angular/forms';
import { ToggleValueFormat } from '../../../models/base-button-toggle';
import { VersionToggleService } from '../../../services/version-toggle';

@Component({
    standalone: false,
    selector: 'por-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FiltersComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly breakpointObserver = inject(BreakpointObserver);
    readonly versionToggle = inject(VersionToggleService);
    private readonly cdn = inject(ChangeDetectorRef);

    @Input() enableExport = true;
    @Input() exportButtonEnabled = true;
    onSmallScreen = false;
    selectedFilters: string[] = [];
    multiCheckForm = new FormGroup({});
    enableForms = false;
    search = '';
    dates: DateFilterOutput = { startDate: null, endDate: null };
    singleFilter = '';
    @Input() searchPlaceholder: string | undefined;
    @Input() uiUrl: string | undefined;

    @Input() dateLabel!: string;
    @Input() disableSearch = false;
    @Input() enableSearchBox = true;

    @Output() readonly exportCSV = new EventEmitter();
    @Input() isProcessing = false;
    @ViewChild(DateFilterComponent) dateFilterComp!: DateFilterComponent;

    toggleMobileFilter = false;
    subscriptions: Subscription[] = [];
    @Output() readonly filterOutput = new EventEmitter<FilterOutput>();
    @Output() readonly getSingleFilter = new EventEmitter<string>();
    @Input() filters: Filter[] = [];
    @Input() initFilters?: string[];
    @Input() multiFilters: Filter[] = [];
    @Input() singleFilters: Filter[] = [];
    @Input() singleFiltersDefaultValue?: string;
    @Input() showClearSingleFilter = true;
    @Input() singleFilterAsToggle = false;
    @Input() enableDateSearchBox = true;
    @Input() startDateLabel = 'start';
    @Input() endDateLabel = 'end';
    @Input() startDateLabelMobile = 'startDate';
    @Input() endDateLabelMobile = 'End Date';

    ngOnInit(): void {
        // checking window size
        this.subscriptions.push(
            this.breakpointObserver
                .observe(['(max-width: 992px)'])
                .pipe(
                    tap((value: BreakpointState) => (this.onSmallScreen = value.matches)),
                    distinctUntilChanged()
                )
                .subscribe()
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initFilters'] && changes['initFilters'].currentValue !== changes['initFilters'].previousValue) {
            this.selectedFilters = [...(this.initFilters || [])];
            Object.keys(this.multiCheckForm.controls).forEach(key => {
                const control = this.multiCheckForm.get(key) as FormControl;
                if (control) {
                    control.setValue(this.selectedFilters.includes(key));
                }
            });
            this.emitOutput();
            this.cdn.detectChanges();
        }
    }

    ngAfterViewInit(): void {
        this.multiFilters.map(fltr => {
            if (fltr) {
                const value = this.initFilters?.includes(fltr.value) ? true : false;
                this.multiCheckForm.addControl(`${fltr.value}`, new FormControl({ value, disabled: fltr.disabled ?? false }));
            }
        });
        this.enableForms = true;

        if (this.initFilters) {
            this.selectedFilters = [...this.initFilters];
            this.enableForms = false;
            setTimeout(() => {
                this.enableForms = true;
                this.cdn.detectChanges();
            });
        } else {
            this.cdn.detectChanges();
        }

        if (this.singleFiltersDefaultValue) {
            this.singleFilter = this.singleFiltersDefaultValue;
        }
    }

    clearFilter() {
        this.dateFilterComp.clearDates();
        this.toggleMobileFilter = false;
        setTimeout(() => {
            this.emitOutput();
        }, 500);
    }

    clearSingleFilter() {
        // Reset to the consumer's configured default so the radio/toggle control and the filtered list agree.
        // Consumers map '' to their own default for the list, so leaving '' here would show a filtered list
        // with no option selected.
        this.singleFilter = this.singleFiltersDefaultValue ?? '';
        this.emitOutput();
    }

    setMultiFilters($e: { value: boolean }, value: string) {
        const formData = this.multiCheckForm.getRawValue();
        const hasShowAll = Object.prototype.hasOwnProperty.call(formData, 'showAll');
        this.enableForms = false;
        let model: Record<string, boolean> = {};

        if (value === 'showAll' && $e.value) {
            if (formData['showAll' as keyof typeof formData]) {
                Object.keys(formData).map(key => {
                    model = {
                        ...model,
                        [key]: false,
                        showAll: true
                    };
                });
            } else {
                model = {
                    ...formData,
                    showAll: false
                };
            }
        } else {
            model = { ...formData };
            if (hasShowAll) {
                model['showAll'] = false;
            }
        }
        this.selectedFilters = [];
        Object.keys(model).map(key => {
            if (model[key as keyof typeof model] && key !== 'showAll' && !this.selectedFilters.includes(key)) {
                this.selectedFilters.push(key);
            }
        });

        // Auto-check showAll if everything else is unchecked, if initFilters default is showAll
        if (hasShowAll && this.selectedFilters.length === 0 && this.initFilters?.includes('showAll')) {
            model['showAll'] = true;
        }

        setTimeout(() => {
            this.enableForms = true;
            this.cdn.detectChanges();
            this.multiCheckForm.setValue(model);
            if (!this.onSmallScreen) {
                this.emitOutput();
            }
        });
    }

    get singleFiltersAsToggleValues(): ToggleValueFormat[] {
        return this.singleFilters.map(filter => ({ text: filter.label, value: filter.value }));
    }

    setSingleFilter($event: { value: string | undefined }) {
        if ($event.value) {
            this.singleFilter = $event.value;
            this.getSingleFilter.emit(this.singleFilter);
            this.emitOutput();
        }
    }

    setSearch(search: string) {
        this.search = search;
        this.emitOutput();
    }

    dateChanged({ startDate, endDate }: DateFilterOutput) {
        this.dates = {
            startDate,
            endDate
        };
        if (!this.onSmallScreen) {
            this.emitOutput();
        }
    }

    emitOutput() {
        if (this.isProcessing) {
            return;
        }

        this.filterOutput.emit({
            multifilters: this.selectedFilters,
            singleFilter: this.singleFilter,
            search: this.search,
            dates: {
                startDate: this.dates.startDate || '',
                endDate: this.dates.endDate || ''
            }
        });
    }

    submit() {
        if (this.onSmallScreen) {
            this.toggleMobileFilter = false;
            this.emitOutput();
        }
    }

    clearCheckedFilters() {
        this.selectedFilters = [];
        this.toggleMobileFilter = false;
        // Reset to the consumer's default rather than '' so the mobile radio group stays in sync
        // with the list after "Clear all" (consumers map '' to their default for the list).
        this.singleFilter = this.singleFiltersDefaultValue ?? '';
        const formData = this.multiCheckForm.value;
        let model = {};
        Object.keys(formData).map(key => {
            model = {
                ...model,
                [key]: false
            };
        });
        this.multiCheckForm.setValue(model);
        this.emitOutput();
    }

    closeFilter() {
        if (this.onSmallScreen) {
            this.toggleMobileFilter = false;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
    }
}
